import { invalidateProjectFinancials } from "@/lib/invalidateProjectFinancials";
import { useState, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export type ImportEntityType = "accounts" | "customers" | "vendors" | "estimates" | "invoices" | "expenses";

export const IMPORT_ENTITIES: { key: ImportEntityType; label: string; icon: string; description: string; dependsOn?: ImportEntityType[] }[] = [
  { key: "accounts", label: "Chart of Accounts", icon: "📊", description: "GL accounts, expense categories" },
  { key: "customers", label: "Customers", icon: "👥", description: "Customer records and contacts" },
  { key: "vendors", label: "Vendors", icon: "🏗️", description: "Subcontractors, suppliers, and vendors" },
  { key: "estimates", label: "Estimates", icon: "📋", description: "Quotes and proposals" },
  { key: "invoices", label: "Invoices", icon: "🧾", description: "Customer invoices and payments" },
  { key: "expenses", label: "Expenses & Bills", icon: "💰", description: "Purchases, bills, and expenses", dependsOn: ["accounts"] },
];

export interface ImportResult {
  accounts: number;
  customers: number;
  vendors: number;
  estimates: number;
  invoices: number;
  expenses: number;
  bills: number;
  skipped_duplicates: number;
  errors: number;
}

export interface ImportJobStatus {
  id: string;
  status: "processing" | "completed" | "failed";
  current_entity: string | null;
  progress: number;
  results: ImportResult | null;
  error: string | null;
  entities: string[];
}

export function useQBImport() {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [jobStatus, setJobStatus] = useState<ImportJobStatus | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qc = useQueryClient();

  const invalidateCaches = () => {
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["estimates"] });
    qc.invalidateQueries({ queryKey: ["project_expenses"] });
    qc.invalidateQueries({ queryKey: ["qb_estimates"] });
    qc.invalidateQueries({ queryKey: ["qb_sync_log"] });
    qc.invalidateQueries({ queryKey: ["chart_of_accounts"] });
    // QB import can touch invoices/bills across many projects — broad invalidation.
    invalidateProjectFinancials(qc);
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const pollJobStatus = useCallback((jobId: string): Promise<ImportResult> => {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        const { data, error } = await supabase
          .from("qb_import_jobs")
          .select("*")
          .eq("id", jobId)
          .single();

        if (error) {
          stopPolling();
          setImporting(false);
          reject(new Error("Failed to check import status"));
          return;
        }

        const status: ImportJobStatus = {
          id: data.id,
          status: data.status as ImportJobStatus["status"],
          current_entity: data.current_entity,
          progress: data.progress,
          results: data.results as unknown as ImportResult | null,
          error: data.error,
          entities: data.entities || [],
        };
        setJobStatus(status);

        if (data.status === "completed") {
          stopPolling();
          setImporting(false);
          const results = data.results as unknown as ImportResult;
          setResult(results);
          invalidateCaches();
          resolve(results);
        } else if (data.status === "failed") {
          stopPolling();
          setImporting(false);
          reject(new Error((data.error as string) || "Import failed"));
        }
      };

      // Poll immediately, then every 2 seconds
      poll();
      pollRef.current = setInterval(poll, 2000);
    });
  }, [stopPolling]);

  /**
   * Start an import job. Returns a promise that resolves when the job completes.
   */
  const runImport = async (companyId: string, entities?: ImportEntityType[], sinceDate?: string): Promise<ImportResult> => {
    setImporting(true);
    setResult(null);
    setJobStatus(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("You must be logged in to import data");

      const body: Record<string, unknown> = { company_id: companyId };
      if (entities && entities.length > 0) body.entities = entities;
      if (sinceDate) body.since_date = sinceDate;

      const res = await fetch(`${SUPABASE_URL}/functions/v1/qb-import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Import failed");

      // data now contains { job_id } — poll for completion
      return await pollJobStatus(data.job_id);
    } catch (err: any) {
      setImporting(false);
      toast.error(err.message || "QuickBooks import failed");
      throw err;
    }
  };

  /**
   * Import a single entity type. Used by the wizard for sequential per-entity progress.
   */
  const runEntityImport = async (companyId: string, entity: ImportEntityType, sinceDate?: string): Promise<ImportResult> => {
    setJobStatus(null);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error("You must be logged in to import data");

    const body: Record<string, any> = { company_id: companyId, entities: [entity] };
    if (sinceDate) body.since_date = sinceDate;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/qb-import`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Import of ${entity} failed`);

    // Poll for completion
    const result = await pollJobStatus(data.job_id);
    invalidateCaches();
    return result;
  };

  return { runImport, runEntityImport, importing, result, jobStatus, stopPolling };
}
